import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Upload, Palette, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoPage = () => {
  const [wraps, setWraps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initDemo = async () => {
      try {
        // Seed demo data if needed
        await axios.post(`${API}/seed-demo`);
        // Fetch demo wraps
        const response = await axios.get(`${API}/wraps/public/demo`);
        setWraps(response.data);
      } catch (error) {
        console.error('Error initializing demo:', error);
      } finally {
        setLoading(false);
      }
    };
    initDemo();
  }, []);

  const featuredWraps = wraps.filter(w => w.is_featured).slice(0, 6);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <section className="pt-32 pb-24" data-testid="demo-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-[0.3em] uppercase text-[#FFFFFF] mb-4 block">Live Demo</span>
            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4">
              Elite Wraps Berlin
            </h1>
            <p className="text-white/60 text-lg">Experience WrapViz as your customers would</p>
          </motion.div>

          {/* Demo Studio Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#0F0F0F] border border-white/10 rounded-sm overflow-hidden mb-16"
          >
            {/* Hero Banner */}
            <div className="relative h-64 md:h-96">
              <img 
                src="https://images.unsplash.com/photo-1635108198165-1773945e506e?w=1600" 
                alt="Demo Studio"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-2">Elite Wraps Berlin</h2>
                <p className="text-white/60">Premium Vehicle Wrapping Studio</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-[#171717] border border-white/5 p-6 rounded-sm text-center card-hover">
                  <Upload className="w-8 h-8 text-[#FFFFFF] mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-semibold mb-2">Upload Your Car</h3>
                  <p className="text-white/40 text-sm mb-4">See your actual car with any wrap</p>
                </div>
                <div className="bg-[#171717] border border-white/5 p-6 rounded-sm text-center card-hover">
                  <Palette className="w-8 h-8 text-[#FFFFFF] mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-semibold mb-2">Browse Wraps</h3>
                  <p className="text-white/40 text-sm mb-4">Explore our full wrap catalog</p>
                </div>
                <div className="bg-[#171717] border border-white/5 p-6 rounded-sm text-center card-hover">
                  <Sparkles className="w-8 h-8 text-[#FFFFFF] mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-semibold mb-2">AI Preview</h3>
                  <p className="text-white/40 text-sm mb-4">Get a photoreal visualization</p>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link to="/s/demo/visualize">
                  <Button 
                    className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0] font-bold uppercase tracking-wider text-sm px-8 py-6 btn-glow"
                    data-testid="try-visualizer-btn"
                  >
                    Try the Visualizer
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Featured Wraps */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-2xl font-bold mb-8">Featured Wraps</h3>
            
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-square bg-[#0F0F0F] rounded-sm animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {featuredWraps.map((wrap) => (
                  <div 
                    key={wrap.id}
                    className="group relative aspect-square bg-[#0F0F0F] border border-white/5 rounded-sm overflow-hidden card-hover cursor-pointer"
                    data-testid={`wrap-${wrap.id}`}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ backgroundColor: wrap.color_hex }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                      <p className="text-sm font-medium truncate">{wrap.name}</p>
                      <p className="text-xs text-white/50 capitalize">{wrap.finish}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 text-center">
              <Link to="/s/demo/wraps">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                  View All Wraps
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-24 text-center bg-[#0F0F0F] border border-white/10 rounded-sm p-12"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Want this for your studio?
            </h3>
            <p className="text-white/60 mb-8">
              Get your own branded website with AI visualization for $149/month
            </p>
            <Link to="/signup">
              <Button 
                className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0] font-bold uppercase tracking-wider text-sm px-8 py-6 btn-glow"
                data-testid="demo-signup-btn"
              >
                Start Your Free Trial
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DemoPage;
